import React from 'react';
import { Box, Paper, Typography, Container, Link, IconButton } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';

interface ForbiddenSubscriptionMessageProps {
  onClose?: () => void;
}

const ForbiddenSubscriptionMessage: React.FC<ForbiddenSubscriptionMessageProps> = ({ onClose }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        overflowY: 'auto',
        flex: 1,
        height: '100%',
        minHeight: 0,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
          borderRadius: '8px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
        },
      }}
    >
      <Container maxWidth='sm' sx={{ py: 2 }}>
        <Paper
          elevation={8}
          sx={{
            p: { xs: 3, sm: 4 },
            textAlign: 'center',
            borderRadius: 4,
            bgcolor: 'white',
            position: 'relative',
          }}
        >
          {/* Close Button */}
          {onClose && (
            <IconButton
              onClick={onClose}
              aria-label="close"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: '#64748b',
                '&:hover': {
                  bgcolor: '#f1f5f9',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}

          {/* Icon Container */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: 64, sm: 80 },
              height: { xs: 64, sm: 80 },
              borderRadius: '50%',
              bgcolor: '#ef4444',
              mb: { xs: 2, sm: 3 },
            }}
          >
            <LockIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'white' }} />
          </Box>

          {/* Title */}
          <Typography
            variant='h4'
            component='h1'
            sx={{
              fontWeight: 700,
              color: '#1e293b',
              mb: 1.5,
              fontSize: { xs: '1.5rem', sm: '2rem' },
            }}
          >
            Access Restricted
          </Typography>

          {/* Subtitle */}
          <Typography
            variant='subtitle1'
            sx={{
              color: '#475569',
              mb: 1,
              fontWeight: 500,
              fontSize: { xs: '0.95rem', sm: '1rem' },
            }}
          >
            SAGE is a premium feature
          </Typography>

          {/* Description */}
          <Typography
            variant='body1'
            sx={{
              color: '#64748b',
              mb: { xs: 3, sm: 4 },
              px: { xs: 1, sm: 2 },
              lineHeight: 1.6,
              fontSize: { xs: '0.875rem', sm: '1rem' },
            }}
          >
            Only agents with an active subscription plan can access SAGE.
            Upgrade your account to unlock AI-powered assistance and enhance
            your dealership operations.
          </Typography>

          {/* Support Information */}
          <Box
            sx={{
              mt: { xs: 2, sm: 3 },
              pt: { xs: 2, sm: 3 },
              borderTop: 1,
              borderColor: '#e2e8f0',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <EmailIcon
                  sx={{ fontSize: { xs: 12, sm: 14 }, color: '#94a3b8' }}
                />
                <Typography
                  variant='caption'
                  sx={{
                    color: '#94a3b8',
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  }}
                >
                  Need help? Contact support at
                </Typography>
              </Box>
              <Link
                href='mailto:support@qrbuddy.ai'
                sx={{
                  color: '#3b82f6',
                  textDecoration: 'none',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  overflowWrap: 'break-word',
                  wordWrap: 'break-word',
                  '&:hover': {
                    color: '#2563eb',
                    textDecoration: 'underline',
                  },
                }}
              >
                support@qrbuddy.ai
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForbiddenSubscriptionMessage;
